import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import { useId, useMemo, useState } from 'react';
import { Controller } from 'react-hook-form';
import type {
  FormCollectionColumnDescriptor,
  FormFieldDescriptor,
  FormOptionDescriptor,
} from '@finance/contracts';
import { Decimal } from '@finance/domain-core';
import type { FieldAdapterProps } from '../../../runtime/forms/field-adapter-registry';
import {
  translateRuntimeTKey,
  translateRuntimeTKeyOrFallback,
} from '../../../i18n/resolve-key';
import { useLocalizedFormatter } from '../../../runtime/formatting/use-localized-formatter';
import { useFieldOptions } from '../../../runtime/forms/use-field-options';

const DECIMAL_PATTERN = /^[+-]?(?:\d+(?:\.\d*)?|\.\d+)$/;

type FormatByType = ReturnType<typeof useLocalizedFormatter>['formatByType'];

function optionLabel(option: FormOptionDescriptor, t: FieldAdapterProps['t']): string {
  if (option.label) return option.label;
  if (option.labelKey) return translateRuntimeTKeyOrFallback(t, option.labelKey);
  return String(option.value);
}

function optionField(column: FormCollectionColumnDescriptor): FormFieldDescriptor {
  return {
    id: `field.collection.${column.name}` as never,
    name: column.name,
    labelKey: column.labelKey,
    fieldType: 'select',
    options: column.options,
    optionSource: column.optionSource,
    value: { hiddenValuePolicy: 'unregister', submitBehavior: 'include' },
  };
}

function emptyRow(columns: FormCollectionColumnDescriptor[]): Record<string, unknown> {
  return Object.fromEntries(columns.map(column => [column.name, column.defaultValue ?? '']));
}

function isEmpty(value: unknown): boolean {
  return value === undefined || value === null || (typeof value === 'string' && value.trim() === '');
}

function isDecimalColumn(column: FormCollectionColumnDescriptor): boolean {
  return ['decimal', 'quantity', 'money'].includes(column.fieldType);
}

function isCellInvalid(column: FormCollectionColumnDescriptor, value: unknown): boolean {
  if (column.required && isEmpty(value)) return true;
  return !isEmpty(value) && isDecimalColumn(column) && !DECIMAL_PATTERN.test(String(value));
}

function sumRows(rows: Record<string, unknown>[], field: string): Decimal {
  return rows.reduce((total, row) => {
    const value = row[field];
    if (isEmpty(value) || !DECIMAL_PATTERN.test(String(value))) return total;
    return total.plus(Decimal.fromString(String(value)));
  }, Decimal.zero());
}

function collectionError(props: FieldAdapterProps, rows: unknown): true | string {
  const { field, t } = props;
  const collection = field.collection;
  if (!collection) return t('form:collection.missingConfiguration');
  if (!Array.isArray(rows)) return t('form:collection.invalid');

  if (collection.minItems !== undefined && rows.length < collection.minItems) {
    return t('form:collection.minimumRows', { count: collection.minItems });
  }
  if (collection.maxItems !== undefined && rows.length > collection.maxItems) {
    return t('form:collection.maximumRows', { count: collection.maxItems });
  }

  for (let rowIndex = 0; rowIndex < rows.length; rowIndex += 1) {
    const row = rows[rowIndex];
    if (!row || typeof row !== 'object' || Array.isArray(row)) {
      return t('form:collection.invalidRow', { index: rowIndex + 1 });
    }
    const rowRecord = row as Record<string, unknown>;
    for (const column of collection.columns) {
      const value = rowRecord[column.name];
      if (column.required && isEmpty(value)) {
        return t('form:collection.requiredCell', {
          index: rowIndex + 1,
          field: translateRuntimeTKeyOrFallback(t, column.labelKey),
        });
      }
      if (!isEmpty(value) && isDecimalColumn(column) && !DECIMAL_PATTERN.test(String(value))) {
        return t('form:collection.invalidDecimal', {
          index: rowIndex + 1,
          field: translateRuntimeTKeyOrFallback(t, column.labelKey),
        });
      }
    }
  }

  if (collection.summaryMode === 'journalBalance') {
    const debit = sumRows(rows as Record<string, unknown>[], 'debit');
    const credit = sumRows(rows as Record<string, unknown>[], 'credit');
    if (!debit.eq(credit)) return t('form:collection.journalUnbalanced');
  }

  return true;
}

function decimalInputProps(fieldType: FormCollectionColumnDescriptor['fieldType']) {
  if (!['decimal', 'quantity', 'money'].includes(fieldType)) return {};
  return {
    inputMode: 'decimal' as const,
    pattern: '[+-]?(?:[0-9]+(?:[.][0-9]*)?|[.][0-9]+)',
    autoComplete: 'off',
    spellCheck: false,
  };
}

function formatReadonlyValue(
  column: FormCollectionColumnDescriptor,
  value: unknown,
  t: FieldAdapterProps['t'],
  formatByType: FormatByType,
): string {
  if (isEmpty(value)) return '—';
  switch (column.fieldType) {
    case 'select': {
      const option = column.options?.find(candidate => String(candidate.value) === String(value));
      return option ? optionLabel(option, t) : String(value);
    }
    case 'date':
      return formatByType(value, 'date');
    case 'number':
      return formatByType(value, 'integer');
    case 'decimal':
      return formatByType(value, 'decimal');
    case 'quantity':
      return formatByType(value, 'quantity');
    case 'money':
      return formatByType(value, 'currency');
    default:
      return String(value);
  }
}

function ReadonlySelectValue({
  column,
  value,
  rowValues,
  t,
}: {
  column: FormCollectionColumnDescriptor;
  value: unknown;
  rowValues: Record<string, unknown>;
  t: FieldAdapterProps['t'];
}) {
  const field = useMemo(() => optionField(column), [column]);
  const resolved = useFieldOptions(field, rowValues, t);
  if (isEmpty(value)) return <>—</>;
  const option = resolved.options.find(candidate => String(candidate.value) === String(value));
  return <>{option?.label ?? String(value)}</>;
}

function ReadonlyCollectionCell({
  column,
  value,
  rowValues,
  t,
  formatByType,
  compact = false,
}: {
  column: FormCollectionColumnDescriptor;
  value: unknown;
  rowValues: Record<string, unknown>;
  t: FieldAdapterProps['t'];
  formatByType: FormatByType;
  compact?: boolean;
}) {
  const content = column.fieldType === 'select' && column.optionSource ? (
    <ReadonlySelectValue column={column} value={value} rowValues={rowValues} t={t} />
  ) : (
    formatReadonlyValue(column, value, t, formatByType)
  );
  if (compact) {
    return <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>{content}</Typography>;
  }
  return (
    <Box component="dl" sx={{ m: 0, minWidth: 0 }}>
      <Typography component="dt" variant="caption" color="text.secondary">
        {translateRuntimeTKeyOrFallback(t, column.labelKey)}
      </Typography>
      <Typography
        component="dd"
        variant="body2"
        sx={{ m: 0, mt: 0.5, fontWeight: 600, overflowWrap: 'anywhere' }}
      >
        {content}
      </Typography>
    </Box>
  );
}

function SearchableCollectionSelect({
  column,
  value,
  rowValues,
  disabled,
  onChange,
  t,
  compact,
  invalid,
}: {
  column: FormCollectionColumnDescriptor;
  value: unknown;
  rowValues: Record<string, unknown>;
  disabled: boolean;
  onChange: (value: unknown) => void;
  t: FieldAdapterProps['t'];
  compact: boolean;
  invalid: boolean;
}) {
  const field = useMemo(() => optionField(column), [column]);
  const resolved = useFieldOptions(field, rowValues, t);
  const label = translateRuntimeTKeyOrFallback(t, column.labelKey);
  const placeholder = column.placeholderKey
    ? translateRuntimeTKey(t, column.placeholderKey)
    : undefined;
  const [inputValue, setInputValue] = useState('');
  const minimumSearchLength = column.optionSource?.minimumSearchLength ?? 0;
  const searchReady = inputValue.length >= minimumSearchLength;
  const options = useMemo(() => {
    if (!searchReady && minimumSearchLength > 0) return [];
    const query = inputValue.trim().toLocaleLowerCase();
    if (!query) return resolved.options;
    return resolved.options.filter(option =>
      `${option.label} ${option.description ?? ''}`.toLocaleLowerCase().includes(query),
    );
  }, [inputValue, minimumSearchLength, resolved.options, searchReady]);
  const selected =
    resolved.options.find(option => String(option.value) === String(value ?? '')) ??
    (isEmpty(value) ? null : { value: String(value), label: String(value) });

  return (
    <Autocomplete
      options={options}
      value={selected}
      inputValue={inputValue}
      onInputChange={(_event, nextInput, reason) => {
        if (reason !== 'reset') setInputValue(nextInput);
      }}
      onChange={(_event, nextValue) => onChange(nextValue?.value ?? '')}
      getOptionLabel={option => option.label}
      isOptionEqualToValue={(option, candidate) => String(option.value) === String(candidate.value)}
      getOptionDisabled={option => Boolean(option.disabled)}
      filterOptions={candidates => candidates}
      loading={resolved.loading}
      disabled={disabled}
      size="small"
      fullWidth
      noOptionsText={t('form:noOptionsAvailable')}
      renderOption={(props, option) => (
        <Box component="li" {...props} key={String(option.value)}>
          <ListItemText primary={option.label} secondary={option.description} />
        </Box>
      )}
      renderInput={params => (
        <TextField
          {...params}
          label={compact ? undefined : label}
          placeholder={placeholder ?? (compact ? label : undefined)}
          required={column.required}
          error={invalid || Boolean(resolved.error)}
          helperText={compact ? undefined : resolved.error}
          slotProps={{
            ...params.slotProps,
            htmlInput: {
              ...params.slotProps?.htmlInput,
              'aria-label': label,
              'aria-required': column.required,
            },
            input: {
              ...params.slotProps?.input,
              endAdornment: (
                <>
                  {resolved.loading && <CircularProgress size={16} />}
                  {params.slotProps?.input?.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
    />
  );
}

function CollectionSelectCell({
  column,
  value,
  rowValues,
  disabled,
  onChange,
  t,
  compact = false,
  invalid = false,
}: {
  column: FormCollectionColumnDescriptor;
  value: unknown;
  rowValues: Record<string, unknown>;
  disabled: boolean;
  onChange: (value: unknown) => void;
  t: FieldAdapterProps['t'];
  compact?: boolean;
  invalid?: boolean;
}) {
  if (column.optionSource?.allowSearch) {
    return (
      <SearchableCollectionSelect
        column={column}
        value={value}
        rowValues={rowValues}
        disabled={disabled}
        onChange={onChange}
        t={t}
        compact={compact}
        invalid={invalid}
      />
    );
  }
  const labelId = useId();
  const label = translateRuntimeTKeyOrFallback(t, column.labelKey);
  const helpText = column.helpTextKey ? translateRuntimeTKey(t, column.helpTextKey) : undefined;
  const field = useMemo(() => optionField(column), [column]);
  const resolved = useFieldOptions(field, rowValues, t);
  const effectiveDisabled = disabled || resolved.loading;

  return (
    <FormControl
      fullWidth
      size="small"
      required={column.required}
      disabled={effectiveDisabled}
      error={invalid || Boolean(resolved.error)}
    >
      {!compact && <InputLabel id={labelId}>{label}</InputLabel>}
      <Select
        labelId={compact ? undefined : labelId}
        label={compact ? undefined : label}
        value={value == null ? '' : String(value)}
        onChange={event => onChange(event.target.value)}
        inputProps={{ 'aria-label': label }}
        displayEmpty={compact}
      >
        {compact && <MenuItem value="" disabled>{label}</MenuItem>}
        {resolved.loading && (
          <MenuItem disabled>
            <CircularProgress size={16} sx={{ mr: 1 }} />
            {t('common:loading')}
          </MenuItem>
        )}
        {!resolved.loading && resolved.options.length === 0 && (
          <MenuItem disabled>{t('form:noOptionsAvailable')}</MenuItem>
        )}
        {!resolved.loading && resolved.options.map(option => (
          <MenuItem key={String(option.value)} value={String(option.value)} disabled={option.disabled}>
            <ListItemText primary={option.label} secondary={option.description} />
          </MenuItem>
        ))}
      </Select>
      {!compact && (resolved.error || helpText) && (
        <FormHelperText>{resolved.error ?? helpText}</FormHelperText>
      )}
    </FormControl>
  );
}

function CollectionCell({
  column,
  value,
  rowValues,
  disabled,
  onChange,
  t,
  compact = false,
}: {
  column: FormCollectionColumnDescriptor;
  value: unknown;
  rowValues: Record<string, unknown>;
  disabled: boolean;
  onChange: (value: unknown) => void;
  t: FieldAdapterProps['t'];
  compact?: boolean;
}) {
  const labelId = useId();
  const label = translateRuntimeTKeyOrFallback(t, column.labelKey);
  const placeholder = column.placeholderKey
    ? translateRuntimeTKey(t, column.placeholderKey)
    : undefined;
  const helpText = column.helpTextKey ? translateRuntimeTKey(t, column.helpTextKey) : undefined;
  const invalid = isCellInvalid(column, value);

  if (column.fieldType === 'select') {
    if (column.optionSource) {
      return (
        <CollectionSelectCell
          column={column}
          value={value}
          rowValues={rowValues}
          disabled={disabled}
          onChange={onChange}
          t={t}
          compact={compact}
          invalid={invalid}
        />
      );
    }
    return (
      <FormControl fullWidth size="small" required={column.required} disabled={disabled} error={invalid}>
        {!compact && <InputLabel id={labelId}>{label}</InputLabel>}
        <Select
          labelId={compact ? undefined : labelId}
          label={compact ? undefined : label}
          value={value == null ? '' : String(value)}
          onChange={event => onChange(event.target.value)}
          inputProps={{ 'aria-label': label }}
          displayEmpty={compact}
        >
          {compact && <MenuItem value="" disabled>{label}</MenuItem>}
          {(column.options ?? []).map(option => (
            <MenuItem key={String(option.value)} value={String(option.value)} disabled={option.disabled}>
              {optionLabel(option, t)}
            </MenuItem>
          ))}
        </Select>
        {!compact && helpText && <FormHelperText>{helpText}</FormHelperText>}
      </FormControl>
    );
  }

  const isNumber = column.fieldType === 'number';
  return (
    <TextField
      fullWidth
      size="small"
      type={isNumber ? 'number' : column.fieldType === 'date' ? 'date' : 'text'}
      label={compact ? undefined : label}
      value={value ?? ''}
      required={column.required}
      disabled={disabled}
      error={invalid}
      placeholder={placeholder ?? (compact && column.fieldType !== 'date' ? label : undefined)}
      helperText={compact ? undefined : helpText}
      onChange={event => {
        if (!isNumber || event.target.value === '') {
          onChange(event.target.value);
          return;
        }
        const parsed = Number(event.target.value);
        onChange(Number.isNaN(parsed) ? event.target.value : parsed);
      }}
      slotProps={{
        htmlInput: {
          'aria-label': label,
          'aria-required': column.required,
          ...(isNumber
            ? { inputMode: 'numeric' as const, step: 1 }
            : decimalInputProps(column.fieldType)),
        },
      }}
    />
  );
}

export function CollectionFieldAdapter(props: FieldAdapterProps) {
  const { field, state, control, formState, serverError, pending, t } = props;
  const { formatByType } = useLocalizedFormatter();
  const theme = useTheme();
  const narrow = useMediaQuery(theme.breakpoints.down('md'));
  const collection = field.collection;
  const error = formState.errors[field.name]?.message as string | undefined;
  const translatedServerError = serverError
    ? translateRuntimeTKeyOrFallback(t, serverError)
    : undefined;
  const helperText =
    error
    ?? translatedServerError
    ?? (field.helpTextKey ? translateRuntimeTKey(t, field.helpTextKey) : undefined);

  if (!collection || !control) {
    return <Alert severity="error">{t('form:collection.missingConfiguration')}</Alert>;
  }

  return (
    <Controller
      name={field.name}
      control={control}
      defaultValue={Array.isArray(field.value?.defaultValue) ? field.value.defaultValue : []}
      rules={{ validate: rows => collectionError(props, rows) }}
      render={({ field: controllerField }) => {
        const rows = Array.isArray(controllerField.value)
          ? (controllerField.value as Record<string, unknown>[])
          : [];
        const readOnly = state.readonly;
        const interactionDisabled = pending || (state.disabled && !readOnly);
        const canAdd =
          !readOnly &&
          !interactionDisabled &&
          (collection.maxItems === undefined || rows.length < collection.maxItems);
        const canRemove =
          !readOnly && !interactionDisabled && rows.length > (collection.minItems ?? 0);
        const debit = collection.summaryMode === 'journalBalance' ? sumRows(rows, 'debit') : undefined;
        const credit = collection.summaryMode === 'journalBalance' ? sumRows(rows, 'credit') : undefined;
        const balanced = debit?.eq(credit ?? Decimal.zero()) ?? true;
        const presentation =
          collection.presentation ?? (collection.summaryMode === 'journalBalance' ? 'compactGrid' : 'cards');
        const compact = presentation === 'compactGrid' && !narrow;

        const updateCell = (rowIndex: number, name: string, value: unknown) => {
          controllerField.onChange(
            rows.map((row, index) => (index === rowIndex ? { ...row, [name]: value } : row)),
          );
        };
        const removeRow = (rowIndex: number) =>
          controllerField.onChange(rows.filter((_, index) => index !== rowIndex));

        const fieldLabel = translateRuntimeTKeyOrFallback(t, field.labelKey);
        const addLabel = translateRuntimeTKeyOrFallback(t, collection.addLabelKey);
        const emptyState = translateRuntimeTKeyOrFallback(t, collection.emptyStateKey);
        const removeLabel = translateRuntimeTKeyOrFallback(t, collection.removeLabelKey);

        return (
          <Stack spacing={1.5} sx={{ minWidth: 0 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: { xs: 'flex-start', sm: 'center' },
                justifyContent: 'space-between',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 1,
                minWidth: 0,
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="h6" component="h3" sx={{ overflowWrap: 'anywhere' }}>
                  {fieldLabel}
                </Typography>
                {helperText && (
                  <Typography
                    variant="body2"
                    color={error || translatedServerError ? 'error.main' : 'text.secondary'}
                    sx={{ overflowWrap: 'anywhere' }}
                  >
                    {helperText}
                  </Typography>
                )}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                {collection.summaryMode === 'count' && (
                  <Chip
                    size="small"
                    label={t('form:collection.count', { count: formatByType(rows.length, 'integer') })}
                  />
                )}
                {!readOnly && (
                  <Button
                    type="button"
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    disabled={!canAdd}
                    onClick={() => controllerField.onChange([...rows, emptyRow(collection.columns)])}
                  >
                    {addLabel}
                  </Button>
                )}
              </Box>
            </Box>

            {rows.length === 0 ? (
              <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', bgcolor: 'action.hover' }}>
                <Typography color="text.secondary">{emptyState}</Typography>
              </Paper>
            ) : compact ? (
              <TableContainer component={Paper} variant="outlined" sx={{ maxWidth: '100%' }}>
                <Table size="small" aria-label={fieldLabel}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: 48 }}>#</TableCell>
                      {collection.columns.map(column => (
                        <TableCell key={column.name} sx={{ minWidth: 150, fontWeight: 600 }}>
                          {translateRuntimeTKeyOrFallback(t, column.labelKey)}
                        </TableCell>
                      ))}
                      {!readOnly && <TableCell sx={{ width: 56 }} />}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((row, rowIndex) => (
                      <TableRow key={rowIndex}>
                        <TableCell sx={{ color: 'text.secondary' }}>{rowIndex + 1}</TableCell>
                        {collection.columns.map(column => (
                          <TableCell key={column.name} sx={{ verticalAlign: 'top' }}>
                            {readOnly ? (
                              <ReadonlyCollectionCell
                                column={column}
                                value={row[column.name]}
                                rowValues={row}
                                t={t}
                                formatByType={formatByType}
                                compact
                              />
                            ) : (
                              <CollectionCell
                                column={column}
                                value={row[column.name]}
                                rowValues={row}
                                disabled={interactionDisabled}
                                onChange={value => updateCell(rowIndex, column.name, value)}
                                t={t}
                                compact
                              />
                            )}
                          </TableCell>
                        ))}
                        {!readOnly && (
                          <TableCell align="center">
                            <Tooltip title={removeLabel}>
                              <span>
                                <IconButton
                                  size="small"
                                  aria-label={`${removeLabel} ${rowIndex + 1}`}
                                  disabled={!canRemove}
                                  onClick={() => removeRow(rowIndex)}
                                >
                                  <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Stack spacing={1.5}>
                {rows.map((row, rowIndex) => (
                  <Paper key={rowIndex} variant="outlined" sx={{ p: { xs: 1.5, sm: 2 } }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                        mb: 1.5,
                      }}
                    >
                      <Typography variant="subtitle2">
                        {t('form:collection.row', { index: formatByType(rowIndex + 1, 'integer') })}
                      </Typography>
                      {!readOnly && (
                        <Tooltip title={removeLabel}>
                          <span>
                            <IconButton
                              size="small"
                              aria-label={`${removeLabel} ${rowIndex + 1}`}
                              disabled={!canRemove}
                              onClick={() => removeRow(rowIndex)}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                    </Box>
                    <Grid container spacing={1.5}>
                      {collection.columns.map(column => (
                        <Grid key={column.name} size={{ xs: 12, md: 6 }}>
                          {readOnly ? (
                            <ReadonlyCollectionCell
                              column={column}
                              value={row[column.name]}
                              rowValues={row}
                              t={t}
                              formatByType={formatByType}
                            />
                          ) : (
                            <CollectionCell
                              column={column}
                              value={row[column.name]}
                              rowValues={row}
                              disabled={interactionDisabled}
                              onChange={value => updateCell(rowIndex, column.name, value)}
                              t={t}
                            />
                          )}
                        </Grid>
                      ))}
                    </Grid>
                  </Paper>
                ))}
              </Stack>
            )}

            {collection.summaryMode === 'journalBalance' && debit && credit && (
              <Paper variant="outlined" sx={{ p: 1.5, bgcolor: balanced ? 'success.50' : 'error.50' }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ justifyContent: 'space-between' }}>
                  <Typography variant="body2">
                    {t('form:collection.journalSummary', {
                      debit: formatByType(debit.toString(), 'decimal'),
                      credit: formatByType(credit.toString(), 'decimal'),
                    })}
                  </Typography>
                  {!balanced && (
                    <Typography variant="body2" color="error.main" sx={{ fontWeight: 600 }}>
                      {t('form:collection.journalUnbalanced')}
                    </Typography>
                  )}
                </Stack>
              </Paper>
            )}
          </Stack>
        );
      }}
    />
  );
}
